import { ArrowLeft, Droplets, Info, Watch } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate } from "react-router-dom";

const SpO2Detail = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Blood Oxygen (SpO2)
          </h1>
        </div>

        {/* Sleep Tracking Notice */}
        <Card className="border-warning/50 bg-warning/10 rounded-xl border-2 p-4">
          <div className="flex items-start gap-3">
            <div className="bg-warning/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
              <Watch className="text-warning h-5 w-5" />
            </div>
            <div>
              <h4 className="mb-1 text-sm font-semibold text-foreground">
                Sleep Tracking Required
              </h4>
              <p className="text-xs text-muted-foreground">
                SpO2 is recorded only when you wear your tracker watch while
                sleeping. Make sure to wear it overnight for accurate readings.
              </p>
            </div>
          </div>
        </Card>

        {/* Current SpO2 */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-info/10 flex h-16 w-16 items-center justify-center rounded-xl">
              <Droplets className="text-info h-8 w-8" />
            </div>
            <div>
              <p className="mb-1 text-sm text-muted-foreground">Average SpO2</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-foreground">97</span>
                <span className="text-lg text-muted-foreground">%</span>
              </div>
            </div>
          </div>
        </Card>

        {/* What is SpO2 */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">What is SpO2?</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">
                Blood Oxygen Saturation (SpO2)
              </strong>{" "}
              measures the percentage of oxygen-carrying hemoglobin in your
              blood compared to the total amount of hemoglobin.
            </p>
            <p>
              <strong className="text-foreground">Normal levels</strong> are
              typically between 95-100%. Your body needs a certain level of
              oxygen in your blood to function properly.
            </p>
            <p>
              <strong className="text-foreground">During sleep</strong>, SpO2
              can naturally dip slightly. Significant or prolonged drops may
              indicate sleep apnea or other respiratory conditions and should be
              discussed with a healthcare provider.
            </p>
          </div>
        </Card>

        {/* When to Be Concerned */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">
            When to Be Concerned
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-warning">•</span>
              <span>Consistently below 95% during sleep</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning">•</span>
              <span>Frequent dips below 90%</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning">•</span>
              <span>Symptoms like shortness of breath or fatigue</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning">•</span>
              <span>Snoring or gasping during sleep</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default SpO2Detail;
